import { useNavigate } from 'react-router-dom';
import { LoginForm } from '../components/LoginForm';
import { useSystemAuth } from './SystemAuthContext';

/**
 * The platform console's sign-in.
 *
 * SAME FORM as the portal login - <LoginForm> is shared, so the two look
 * and behave identically and only have to be restyled once.
 *
 * What is NOT shared is everything behind the submit: this calls
 * /api/system/auth/login, which authenticates against `system_admins` with
 * its own JWT secrets and stores its token under its own key. A single
 * form component cannot blur that, because it never touches an API client.
 *
 * Not linked from anywhere either. An operator types the URL; nothing in
 * the product advertises that the console exists.
 */
export default function SystemLoginPage() {
  const { signIn } = useSystemAuth();
  const navigate = useNavigate();

  return (
    <LoginForm
      title="Platform Console"
      subtitle="Operator sign-in. This is not the portal login."
      footnote="Five attempts per 15 minutes. Every attempt is recorded."
      identifierField="email"
      onSubmit={async (identifier, password) => {
        await signIn(identifier, password);
        navigate('/system/organisations', { replace: true });
      }}
      /*
       * 503 deserves its own wording. It means the server has no
       * SYSTEM_JWT_* configuration, which no amount of retrying will fix -
       * and reading "invalid email or password" while the password is in
       * fact correct is the most confusing failure this screen can show.
       */
      describeError={(error) => {
        const code = (error as { code?: unknown } | null)?.code;
        return code === 'NOT_CONFIGURED'
          ? 'The platform console is not configured on this server. '
            + 'The SYSTEM_JWT_* environment values are unset.'
          : null;
      }}
    />
  );
}
